import { Box, List, ListItem, ListItemButton, Typography } from '@mui/material';
import { VersionChip } from '~/components/Chip/VersionChip';
import LabeledList from '~/components/LabeledList';
import {
    formatModalityHint,
    PredictionModel,
} from '~/utils/transforms/predictionModelsDataTransforms';
import LoadingModels from './LoadingModels';
import NoModelsFound from './NoModelsFound';

interface AvailableModelsProps {
    models: PredictionModel[];
    selectedModel?: PredictionModel;
    isLoading?: boolean;
    emptyMessage?: string;
    labelAdornment?: React.ReactNode;
    onSelectedModelChanged: (model: PredictionModel) => void;
}

export default function AvailableModels({
    models,
    selectedModel,
    onSelectedModelChanged,
    isLoading,
    emptyMessage,
    labelAdornment,
}: AvailableModelsProps) {
    if (isLoading) {
        return <LoadingModels />;
    } else if (models.length <= 0) {
        return <NoModelsFound message={emptyMessage} />;
    }

    return (
        <LabeledList label="Model" adornment={labelAdornment}>
            <List>
                {models.map((model) => {
                    const modalityHint = formatModalityHint(model);
                    return (
                        <ListItem key={model.name} disablePadding>
                            <ListItemButton
                                onClick={() => onSelectedModelChanged(model)}
                                selected={selectedModel?.name === model.name}
                            >
                                <Box
                                    display="grid"
                                    gridTemplateColumns="1fr auto"
                                    width="100%"
                                    alignItems="center"
                                    gap={2}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="baseline"
                                        gap={0.75}
                                        minWidth={0}
                                    >
                                        <Typography fontSize={14} noWrap>
                                            {model.label}
                                        </Typography>
                                        {modalityHint && (
                                            <Typography
                                                fontSize={12}
                                                color="text.secondary"
                                                noWrap
                                            >
                                                ({modalityHint})
                                            </Typography>
                                        )}
                                    </Box>
                                    {model.version && (
                                        <VersionChip
                                            version={model.version}
                                            isLatest={model.isLatest}
                                        />
                                    )}
                                </Box>
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </LabeledList>
    );
}
